import { formatDistance } from 'date-fns';
import { AlertCircle, CheckCircle2, Loader, PowerOff } from 'lucide-react';
import { PartnerChannelBadge } from './PartnerPhaseBadge';
import type { PartnerDiscord } from '../../types/partners';
import { cn } from '../../utils/cn';

interface PartnerSyncStatusProps {
  discord: PartnerDiscord;
  /** The time relative labels are measured from (ms). The page's clock, so everything agrees. */
  now: number;
  className?: string;
}

/**
 * Where Discord stands for one partner: the channel as Mr. Roboto last saw
 * it, whether the latest change has been applied yet, when it last reported,
 * and the last thing that went wrong.
 */
export const PartnerSyncStatus = ({ discord, now, className }: PartnerSyncStatusProps) => {
  if (!discord.managed) {
    return (
      <div className={cn('flex items-start gap-2 text-sm text-glass-400', className)}>
        <PowerOff className="w-4 h-4 mt-0.5 shrink-0 text-glass-500" />
        <p>Discord is not managed for this partner. The team is kept here, but Mr. Roboto changes nothing in Discord until management is on.</p>
      </div>
    );
  }

  const name = discord.observed_detail?.name;
  const category = discord.observed_detail?.category_name;
  const seen = discord.observed_at ? formatDistance(new Date(discord.observed_at), new Date(now), { addSuffix: true }) : null;

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm">
        <PartnerChannelBadge state={discord.observed_state} />
        {name && (
          <span className="text-glass-200">
            <span className="font-mono">#{name}</span>
            {category && <span className="text-glass-400"> in {category}</span>}
          </span>
        )}
        {discord.pending ? (
          <span
            className="inline-flex items-center gap-1.5 text-amber-300"
            title="Mr. Roboto has not reported applying the latest change yet. This page checks every few seconds while it waits."
          >
            <Loader className="w-3.5 h-3.5 animate-spin" />
            Waiting for Mr. Roboto to apply the latest change
          </span>
        ) : (
          discord.observed_at && (
            <span className="inline-flex items-center gap-1.5 text-green-400">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Up to date
            </span>
          )
        )}
        <span className="text-xs text-glass-500 tabular-nums">{seen ? `Last reported ${seen}` : 'Mr. Roboto has not reported on this partner yet'}</span>
      </div>
      {discord.last_error && (
        <div className="alert alert-danger" role="alert">
          <AlertCircle className="w-4 h-4" />
          <span className="min-w-0">
            <span className="font-medium">Last problem: </span>
            {discord.last_error}
          </span>
        </div>
      )}
    </div>
  );
};
